/**
 * Single source of truth for every piece of portfolio content.
 * Apps read from here so copy only ever lives in one place.
 */

export const PROFILE = {
  name: "Miracle Aponjolosun",
  handle: "earlpappi",
  role: "Fullstack Developer",
  avatar: "https://avatars.githubusercontent.com/u/94018014?v=4",
  bio: "Fullstack developer with 5 years of experience building scalable, end-to-end web applications. Strong focus on React, Next.js and Node.js — from UI to production APIs.",
  location: "Lagos, Nigeria",
  email: "maponjolosun@gmail.com",
  phone: "+234 (814) 540-6839",
  phoneHref: "tel:+2348145406839",
  github: "EarlPappi",
  resume: "/miracle_aponjolosun.pdf",
};

export const SOCIALS = [
  { label: "GitHub", handle: "@EarlPappi", url: "https://github.com/EarlPappi" },
  { label: "X", handle: "@earl_pappi", url: "https://twitter.com/earl_pappi" },
  {
    label: "LinkedIn",
    handle: "miracle-aponjolosun",
    url: "https://www.linkedin.com/in/miracle-aponjolosun-793519182/",
  },
];

export const CURRENTLY = [
  {
    id: "c1",
    title: "Frontend Engineer @ CapitalSage",
    meta: "Since May 2025",
    description:
      "Building scalable, high-performance UI components using Next.js and TailwindCSS. Leading the frontend team and optimizing builds for rapid deployment.",
  },
  {
    id: "c2",
    title: "Postgraduate Student @ Babcock University",
    meta: "Since January 2026",
    description: "Studying for a master's degree in Computer Science.",
  },
  {
    id: "c3",
    title: "Freelance Developer",
    meta: "Ongoing",
    description:
      "Continuously shipping production-ready applications with a focus on React, Next.js and modern web technologies.",
    link: { label: "github.com/EarlPappi", url: "https://github.com/EarlPappi" },
  },
  {
    id: "c4",
    title: "Amateur Table Tennis Player",
    meta: "Since 2021",
    description:
      "Playing table tennis for fun and fitness, actively competing in local tournaments.",
  },
];

export const PAST_LIFE = [
  {
    id: "p1",
    title: "Fullstack Developer @ Pureworker",
    meta: "March 2024 — September 2024",
    description:
      "Led the frontend team building responsive interfaces and a reusable component library. Drove a 22% retention lift via mobile optimization and 84% organic traffic growth through SEO.",
  },
  {
    id: "p2",
    title: "Frontend Engineer @ African Founders",
    meta: "September 2022 — January 2024",
    description:
      "Led the frontend team shipping responsive screens and shared components. Boosted user retention by 19% with mobile-first optimization and grew organic traffic 30% via end-to-end SEO.",
  },
  {
    id: "p3",
    title: "Fullstack Developer @ Forenxy",
    meta: "February 2021 — January 2022 · Contract",
    description:
      "Built a crypto exchange platform with a real-time trading UI, order books and wallet integrations. Managed the frontend team and ran code reviews.",
  },
  {
    id: "p4",
    title: "BSc. Agronomy @ University of Ibadan",
    meta: "2016 — 2023 · Ibadan, Nigeria",
    description:
      "Bachelor of Science degree. Balanced academic work with professional development in web technologies.",
  },
];

export const SKILL_GROUPS = [
  {
    label: "Languages",
    items: ["JavaScript (ES6+)", "TypeScript", "Python", "HTML5", "CSS3 / SCSS"],
  },
  {
    label: "Frameworks & Libraries",
    items: ["React", "Next.js", "Node.js", "Redux", "Express"],
  },
  {
    label: "Styling & UI",
    items: ["TailwindCSS", "Material UI", "Bootstrap", "Framer"],
  },
  {
    label: "Tools & Workflow",
    items: ["Git", "GitHub", "Jira", "Slack", "WordPress", "Vercel"],
  },
];

export const PROJECTS = [
  {
    id: "nearestkin",
    shot: "/shots/nearestkin.webp",
    title: "NearestKin",
    kind: "Family connection platform",
    year: "2026",
    description:
      "Keeps families close to someone in prison or jail. Built an IVR the member inside reaches from an ordinary facility phone to hear and reply to messages, take live calls and talk to a built-in AI assistant, all by voice. Outside, family use a web app or a React Native mobile app with VoIP calling and push notifications. Plus an admin console for members, moderation and billing, and Stripe payments behind it all.",
    stack: ["React", "React Native", "Node.js", "PostgreSQL", "Twilio", "Asterisk"],
    url: "https://nearestkin.com/",
  },
  {
    id: "zaltev",
    shot: "/shots/zaltev.webp",
    title: "Zaltev",
    kind: "Multi-tenant trading platform",
    year: "2026",
    description:
      "Multi-tenant SaaS that runs each user's trading strategy in its own isolated worker process. Async FastAPI backend, React dashboard and a separate admin console, two Telegram bots, a browser connector extension, crypto billing and role-based access — shipped as a Docker stack behind Cloudflare.",
    stack: ["FastAPI", "React", "PostgreSQL", "Redis", "Docker"],
    url: "https://zaltev.com/",
  },
  {
    id: "weonline",
    shot: "/shots/weonline.webp",
    title: "WeOnline",
    kind: "Software studio",
    year: "2026",
    description:
      "Site and brand for a senior-led software studio — service catalogue, case studies and the enquiry pipeline behind its five-day MVP engagement. Built end to end.",
    stack: ["Next.js", "TailwindCSS", "Framer Motion"],
    url: "https://weonline.studio/",
  },
  {
    id: "medflow",
    shot: "/shots/medflow.webp",
    title: "MedFlow",
    kind: "Hospital management",
    year: "2025",
    description:
      "Multi-tenant hospital management software running several facilities off one deployment. Patient records, appointments and staff scheduling, plus billing with online payment collection, insurance claim tracking, pharmacy and stock control, lab orders and results, and role-based access down to the department.",
    stack: ["Next.js", "Node.js", "PostgreSQL", "Paystack"],
    url: "https://medflow.earlpappi.com/",
  },
  {
    id: "kindssecurity",
    shot: "/shots/kindssecurity.webp",
    title: "KindsSecurity",
    kind: "Email security platform",
    year: "2025",
    description:
      "Email security solution — built the admin portal, the learners portal and the marketing site.",
    stack: ["Next.js", "TypeScript", "TailwindCSS"],
    url: "https://kindssecurity.com/",
  },
  {
    id: "t-hrms",
    shot: "/shots/t-hrms.webp",
    title: "T-HRMS",
    kind: "HR management system",
    year: "2024",
    description:
      "A human resource management system built with Next.js and Node.js. Demo login — admin@thrm.com / Admin@123456.",
    stack: ["Next.js", "Node.js", "MongoDB"],
    url: "https://t-hrm.vercel.app/",
  },
  {
    id: "blaze",
    shot: "/shots/blaze.webp",
    title: "Blaze.money (reimagined)",
    kind: "Fintech marketing site",
    year: "2024",
    description:
      "Rebuilt the existing blaze.money site from scratch — improved UI, performance and overall experience while preserving the brand.",
    stack: ["React", "TailwindCSS", "Framer Motion"],
    url: "https://blaze-main.vercel.app/",
  },
  {
    id: "j-em",
    shot: "/shots/j-em.webp",
    title: "J-EM (now Rivages)",
    kind: "Corporate website",
    year: "2023",
    description:
      "Official website for J-EM, a Canadian SME. Handled the build and the SEO end to end.",
    stack: ["React", "SEO", "WordPress"],
    url: "https://j-em.ca/",
  },
];

export const STATS = [
  { label: "Years shipping", value: "5+" },
  { label: "Featured projects", value: String(PROJECTS.length) },
  { label: "Frontend teams led", value: "4" },
  { label: "Based in", value: "Lagos" },
];
